import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { reportsAPI, leavesAPI, shiftsAPI, assignmentsAPI } from '../utils/api';
import { format } from 'date-fns';
import { fr, enCA } from 'date-fns/locale';
import i18n from '../i18n';

const StatCard = ({ icon, value, label, variant = '', onClick }) => (
  <button
    type="button"
    className={`stat-card ${variant}`}
    onClick={onClick}
    style={{
      width: '100%',
      textAlign: 'left',
      cursor: onClick ? 'pointer' : 'default',
      background: 'white',
    }}
  >
    <div className="stat-icon">{icon}</div>
    <div className="stat-value">{value ?? '—'}</div>
    <div className="stat-label">{label}</div>
  </button>
);

const QuickAction = ({ icon, label, onClick }) => (
  <button className="card" style={{ padding: '20px', textAlign: 'center', cursor: 'pointer', border: '1.5px solid var(--gray-200)', background: 'white', transition: 'all 0.15s', width: '100%' }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(13,148,136,0.1)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-200)'; e.currentTarget.style.boxShadow = ''; }}
    onClick={onClick}>
    <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>{label}</div>
  </button>
);

const DashboardPage = () => {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [todayShifts, setTodayShifts] = useState([]);
  const [todayShiftsCount, setTodayShiftsCount] = useState(0);
  const [staffPatients, setStaffPatients] = useState([]);
  const [presentNow, setPresentNow] = useState([]);
  const [loading, setLoading] = useState(true);
  const locale = i18n.language.startsWith('fr') ? fr : enCA;

  useEffect(() => {
    const load = async () => {
      try {
        if (hasRole('admin', 'manager')) {
          const [sumRes, leavesRes, presentRes] = await Promise.all([
            reportsAPI.getSummary(),
            leavesAPI.getAll({ status: 'pending' }),
            reportsAPI.getPresentNow(),
          ]);
          setSummary(sumRes.data);
          setRecentLeaves(leavesRes.data.slice(0, 5));
          setPresentNow(presentRes.data || []);
        }

        const today = new Date();
        const start = format(today, "yyyy-MM-dd'T'00:00:00");
        const end = format(today, "yyyy-MM-dd'T'23:59:59");
        const shiftsRes = await shiftsAPI.getAll({ start, end });
        const effectiveTodayShifts = (shiftsRes.data || []).filter(s => s.status !== 'cancelled');
        setTodayShiftsCount(effectiveTodayShifts.length);
        setTodayShifts(effectiveTodayShifts.slice(0, 8));

        if (hasRole('staff') && user?.id) {
          const assnRes = await assignmentsAPI.getStaffPatients(user.id, { active: 'true' });
          setStaffPatients(assnRes.data || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hasRole, user?.id]);

  const today = format(new Date(), 'EEEE d MMMM yyyy', { locale });
  const presentNowByCenter = presentNow.reduce((accumulator, row) => {
    const key = row.center_name || 'Centre';
    if (!accumulator[key]) accumulator[key] = [];
    accumulator[key].push(row);
    return accumulator;
  }, {});

  if (loading) return <div className="loading-screen" style={{ height: '50vh' }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('dashboard.welcome')}, {user?.firstName} 👋</h2>
          <p style={{ color: 'var(--gray-500)', marginTop: 4, textTransform: 'capitalize' }}>{today}</p>
        </div>
      </div>

      {/* Stats */}
      {hasRole('admin', 'manager') && summary && (
        <div className="stats-grid">
          <StatCard
            icon="👥"
            value={summary.activeNow}
            label={t('dashboard.activeStaff')}
            onClick={() => navigate('/staff')}
          />
          <StatCard
            icon="🧑‍💼"
            value={summary.activeManagers ?? 0}
            label={t('dashboard.activeManagers')}
            onClick={() => navigate('/staff')}
          />
          <StatCard
            icon="📋"
            value={summary.pendingLeaves}
            label={t('dashboard.pendingLeaves')}
            variant="warning"
            onClick={() => navigate('/leaves')}
          />
          <StatCard
            icon="📅"
            value={todayShiftsCount}
            label={t('dashboard.todayShifts')}
            variant="info"
            onClick={() => navigate('/schedule')}
          />
          <StatCard
            icon="🏥"
            value={summary.totalPatients ?? 0}
            label={t('dashboard.totalPatients')}
            variant="success"
            onClick={() => navigate('/patients')}
          />
        </div>
      )}

      <div className={hasRole('admin', 'manager') ? 'grid-2' : ''} style={{ gap: 24 }}>

        {/* Quick Actions */}
        <div>
          <div className="card">
            <div className="card-header">
              <span className="card-title">{t('dashboard.quickActions')}</span>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {hasRole('admin', 'manager') && <>
                  <QuickAction icon="🪪" label={t('nav.badges')} onClick={() => navigate('/badges')} />
                  <QuickAction icon="📅" label={t('nav.schedule')} onClick={() => navigate('/schedule')} />
                  <QuickAction icon="➕" label={t('dashboard.addStaff')} onClick={() => navigate('/staff')} />
                  <QuickAction icon="🏢" label={t('dashboard.addCenter')} onClick={() => navigate('/centers')} />
                  <QuickAction icon="📋" label={t('dashboard.viewLeaves')} onClick={() => navigate('/leaves')} />
                  <QuickAction icon="📊" label={t('nav.reports')} onClick={() => navigate('/reports')} />
                </>}
                {hasRole('usager') && <>
                  <QuickAction icon="🗓️" label={t('nav.activities')} onClick={() => navigate('/activities')} />
                  <QuickAction icon="❤️" label={t('nav.patients')} onClick={() => navigate('/patients')} />
                  <QuickAction icon="📊" label={t('nav.reports')} onClick={() => navigate('/reports')} />
                  <QuickAction icon="🚐" label={t('nav.transport')} onClick={() => navigate('/transport')} />
                </>}
                {hasRole('staff') && <>
                  <QuickAction icon="❤️" label={t('nav.patients')} onClick={() => navigate('/patients')} />
                  <QuickAction icon="📅" label={t('nav.schedule')} onClick={() => navigate('/schedule')} />
                  <QuickAction icon="🌴" label={t('leaves.requestLeave')} onClick={() => navigate('/leaves')} />
                  <QuickAction icon="📊" label={t('nav.reports')} onClick={() => navigate('/reports')} />
                </>}
              </div>
            </div>
          </div>
        </div>

        {/* Today's shifts + pending leaves */}
        {hasRole('admin', 'manager') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Pending Leaves */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">🌴 {t('leaves.title')} — {t('leaves.pending')}</span>
                <button className="btn btn-sm btn-outline" onClick={() => navigate('/leaves')}>{t('common.view')}</button>
              </div>
              <div style={{ padding: '0 24px 16px' }}>
                {recentLeaves.length === 0 ? (
                  <p style={{ color: 'var(--gray-400)', fontSize: 14, padding: '16px 0' }}>{t('leaves.noLeaves')}</p>
                ) : recentLeaves.map(leave => (
                  <div key={leave.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{leave.first_name} {leave.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {leave.start_date} → {leave.end_date} · {t(`leaves.leaveTypes.${leave.leave_type}`)}
                      </div>
                    </div>
                    <span className="badge badge-warning">{t('leaves.pending')}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Today's Shifts */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">📅 {t('shifts.title')} — {i18n.language.startsWith('fr') ? "Aujourd'hui" : 'Today'}</span>
                <button className="btn btn-sm btn-outline" onClick={() => navigate('/schedule')}>{t('common.view')}</button>
              </div>
              <div style={{ padding: '0 24px 16px' }}>
                {todayShifts.length === 0 ? (
                  <p style={{ color: 'var(--gray-400)', fontSize: 14, padding: '16px 0' }}>{t('shifts.noShifts')}</p>
                ) : todayShifts.map(shift => (
                  <div key={shift.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{shift.first_name} {shift.last_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                      {format(new Date(shift.start_time), 'HH:mm')} – {format(new Date(shift.end_time), 'HH:mm')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <span className="card-title">🟢 Présents maintenant</span>
              </div>
              <div style={{ padding: '0 24px 16px' }}>
                {presentNow.length === 0 ? (
                  <p style={{ color: 'var(--gray-400)', fontSize: 14, padding: '16px 0' }}>Aucun employé pointé en ce moment.</p>
                ) : Object.entries(presentNowByCenter).map(([centerName, items]) => (
                  <div key={centerName} style={{ padding: '10px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{centerName} · {items.length}</div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {items.map((person) => (
                        <div key={person.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{person.first_name} {person.last_name}</div>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                              {person.employee_number || '—'} {person.job_function ? `· ${person.job_function}` : ''}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--gray-500)' }}>
                            <div>Depuis {format(new Date(person.clock_in), 'HH:mm')}</div>
                            <div>{person.hours_elapsed} h</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {hasRole('staff') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div className="card">
              <div className="card-header">
                <span className="card-title">🧑‍🤝‍🧑 Usagers assignés</span>
              </div>
              <div style={{ padding: '0 24px 16px' }}>
                {staffPatients.length === 0 ? (
                  <p style={{ color: 'var(--gray-400)', fontSize: 14, padding: '16px 0' }}>Aucun usager assigné.</p>
                ) : staffPatients.map((a) => (
                  <div key={a.assignment_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{a.patient_first_name} {a.patient_last_name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="badge badge-primary">{a.assignment_type}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => navigate('/patients', { state: { patientId: a.patient_id, openReadOnly: true } })}
                      >
                        Voir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <span className="card-title">📅 {t('shifts.title')} — {i18n.language.startsWith('fr') ? "Aujourd'hui" : 'Today'}</span>
                <button className="btn btn-sm btn-outline" onClick={() => navigate('/schedule')}>{t('common.view')}</button>
              </div>
              <div style={{ padding: '0 24px 16px' }}>
                {todayShifts.length === 0 ? (
                  <p style={{ color: 'var(--gray-400)', fontSize: 14, padding: '16px 0' }}>{t('shifts.noShifts')}</p>
                ) : todayShifts.map(shift => (
                  <div key={shift.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{shift.first_name} {shift.last_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                      {format(new Date(shift.start_time), 'HH:mm')} – {format(new Date(shift.end_time), 'HH:mm')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
